// Header download button — exposes the desktop app installer for the
// current OS. Hidden when the dashboard is already running inside the
// Tauri shell (no point offering to download the thing you're in).
//
// Data comes from `/api/desktop/release` (public endpoint, see
// server/src/routes/desktopRelease.routes.ts). The endpoint proxies
// GitHub Releases for `desktop-v*` tags and is cached server-side, so
// hammering this from the header is cheap.

import { useEffect, useMemo, useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { PATHS } from '@/lib/paths';
import { apiPublic, apiErrorMessage } from '@/lib/api';

interface DesktopRelease {
  version: string | null;
  publishedAt: string | null;
  releaseUrl?: string;
  downloads: {
    windows?: string | null;
    macosArm?: string | null;
    macosIntel?: string | null;
    linuxAppimage?: string | null;
    linuxDeb?: string | null;
  };
}

function IconDownload() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

type GuessedOs = 'windows' | 'macos-arm' | 'macos-intel' | 'linux' | 'unknown';

function guessOs(): GuessedOs {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent.toLowerCase();
  const platform = (navigator.platform || '').toLowerCase();
  if (ua.includes('windows') || platform.includes('win')) return 'windows';
  if (ua.includes('mac') || platform.includes('mac')) {
    // navigator.userAgentData.platform is more reliable for ARM, fall back
    // to UA sniff for older browsers.
    const uaData = (navigator as unknown as { userAgentData?: { platform?: string } }).userAgentData;
    if (uaData?.platform?.toLowerCase().includes('arm')) return 'macos-arm';
    // Apple Silicon Safari still reports x86_64 in UA — we default to ARM
    // since new Macs ship that way, and Intel users can pick from the menu.
    return 'macos-arm';
  }
  if (ua.includes('linux') || platform.includes('linux')) return 'linux';
  return 'unknown';
}

function isInsideTauri(): boolean {
  if (typeof window === 'undefined') return false;
  return Boolean(
    (window as unknown as { __TAURI_INTERNALS__?: unknown }).__TAURI_INTERNALS__
      || (window as unknown as { __TAURI__?: unknown }).__TAURI__,
  );
}

export function DownloadAppButton() {
  const [release, setRelease] = useState<DesktopRelease | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const insideTauri = useMemo(isInsideTauri, []);
  const guessed = useMemo(guessOs, []);

  useEffect(() => {
    if (insideTauri) return;
    let cancelled = false;
    setLoading(true);
    apiPublic<DesktopRelease>(PATHS.desktopRelease)
      .then((data) => {
        if (!cancelled) setRelease(data);
      })
      .catch((e) => {
        if (!cancelled) setError(apiErrorMessage(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [insideTauri]);

  if (insideTauri) return null;

  const downloads = release?.downloads ?? {};
  const primaryUrl =
    guessed === 'windows'     ? downloads.windows
    : guessed === 'macos-arm' ? downloads.macosArm ?? downloads.macosIntel
    : guessed === 'macos-intel' ? downloads.macosIntel ?? downloads.macosArm
    : guessed === 'linux'     ? downloads.linuxAppimage ?? downloads.linuxDeb
    : null;

  const primaryLabel =
    guessed === 'windows'     ? 'Download for Windows'
    : guessed === 'macos-arm' ? 'Download for macOS (Apple Silicon)'
    : guessed === 'macos-intel' ? 'Download for macOS (Intel)'
    : guessed === 'linux'     ? 'Download for Linux'
    : 'Download app';

  // No release yet (CI hasn't tagged desktop-v* once). Render a disabled
  // button with a tooltip so it's visible to QA but doesn't 404 anyone.
  const noReleaseYet = !loading && !error && release && !release.version;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background px-2.5 text-xs text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={loading || !!error || !!noReleaseYet}
        title={
          error ? `Couldn't load release: ${error}`
            : noReleaseYet ? 'Desktop app not built yet — tag desktop-v0.1.0 to publish'
            : 'Download the Bugpunch desktop app'
        }
        aria-label="Download desktop app"
      >
        <IconDownload />
        <span className="hidden md:inline">Download app</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="flex items-center justify-between text-xs">
          <span>Bugpunch Desktop</span>
          {release?.version && (
            <span className="font-mono text-[10px] text-muted-foreground">v{release.version}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {primaryUrl && (
          <>
            <DropdownMenuItem onClick={() => { window.location.href = primaryUrl; }}>
              <IconDownload />
              <span className="ml-2 font-medium">{primaryLabel}</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wide text-muted-foreground">
          All platforms
        </DropdownMenuLabel>
        {downloads.windows && (
          <DropdownMenuItem onClick={() => { window.location.href = downloads.windows!; }}>
            Windows installer
          </DropdownMenuItem>
        )}
        {downloads.macosArm && (
          <DropdownMenuItem onClick={() => { window.location.href = downloads.macosArm!; }}>
            macOS (Apple Silicon)
          </DropdownMenuItem>
        )}
        {downloads.macosIntel && (
          <DropdownMenuItem onClick={() => { window.location.href = downloads.macosIntel!; }}>
            macOS (Intel)
          </DropdownMenuItem>
        )}
        {downloads.linuxAppimage && (
          <DropdownMenuItem onClick={() => { window.location.href = downloads.linuxAppimage!; }}>
            Linux AppImage
          </DropdownMenuItem>
        )}
        {downloads.linuxDeb && (
          <DropdownMenuItem onClick={() => { window.location.href = downloads.linuxDeb!; }}>
            Linux .deb
          </DropdownMenuItem>
        )}
        {release?.releaseUrl && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => window.open(release.releaseUrl!, '_blank', 'noopener,noreferrer')}
              className="text-muted-foreground"
            >
              Release notes &amp; checksums &rarr;
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
